import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { CreateInvoiceFooterProps } from "models/propTypes/invoicePropTypes";
import { HTMLConstants } from "utils/constants";
import { InvoiceFormTypes } from "models/formTypes";
import { ObjectSchema } from "models/utilTypes/yupSchemaTypes";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import {
  InvoiceDraftFooter,
  RecurringInvoiceDraftFooter,
  useInvoiceFormValidations,
  useInvoicePreview,
  useHighInvoiceAmountWarning,
} from "components/invoice";
import { Portal } from "@mui/material";
import { useFormSubmitHelpers } from "components/common/wintForms";
import { useArticleService, useCustomerService, useInvoiceService, useRecurringInvoiceService } from "serviceHooks";
import { useFloatingMessages } from "components/common";
import { InvoiceHelper, LocalizationHelperInstance } from "utils/helpers";
import waitForForwardedCostAttachments from "./waitForForwardedCostAttachments";
//#endregion

const CreateInvoiceFooter: React.FC<CreateInvoiceFooterProps> = (props) => {
  const invoiceService = useInvoiceService();
  const recurringInvoiceService = useRecurringInvoiceService();
  const articleService = useArticleService();
  const customerService = useCustomerService();
  const { enqueueMessage } = useFloatingMessages();
  const { showWarningDialogIfNeeded, USER_CANCELLED_ERROR } = useHighInvoiceAmountWarning();

  const { isRecurring, invoice, reloadInvoice, wizard, form } = props;

  const footerMount = document.getElementById(HTMLConstants.EntityCreationLayoutFooterPortalId);

  const { getValidationSchema } = useInvoiceFormValidations();
  const { submitForm, validateForm } = useFormSubmitHelpers();

  const hideDeliveryDate = useSelector((store: AppState) => store.auth.selectedCompany.hideInvoiceDeliveryDate);
  const noVatCompany = useSelector((store: AppState) => store.auth.selectedCompany.noVat);

  const isRecurringInvoiceDraft =
    isRecurring &&
    (props.invoice?.id === undefined || props.invoice?.status === WintSuperkollAPIModel.RecurringInvoiceState.Created);

  const onInvoiceFormSubmit = (asDraft: boolean, form: InvoiceFormTypes.Create) => {
    if (isRecurring) {
      const invoicePost = recurringInvoiceService.convertFormToAPIRequest(form, noVatCompany);
      return recurringInvoiceService.createOrUpdateInvoice(form.id, invoicePost);
    } else {
      const invoicePost = invoiceService.convertFormToAPIRequest(
        form,
        noVatCompany,
        hideDeliveryDate,
        props.invoice as WintSuperkollAPIModel.Invoice
      );
      return invoiceService.createOrUpdateInvoice(form.id, invoicePost, asDraft);
    }
  };

  // Server-side (ValidateItemRevenueAccountVat) skips this check entirely for credit invoices -
  // a credit row's account/VAT is a carried-forward historical value, not something newly chosen
  // against today's article config, so the server has nothing to validate against. The UI lock is
  // hydrated for existing invoices (see useInvoiceInitialValues.tsx), but the VAT snap-back
  // deliberately exempts credit rows (see InvoiceHelper.getRowsNeedingVatReset), for the same
  // carried-forward-value reason - so "kreditera och redigera" can still leave a row's retyped VAT
  // mismatched against the article. This is a submit-time check reusing InvoiceHelper.isRowVatLocked
  // so a credit invoice with a mismatched row is caught with a clear message instead of silently
  // posting the wrong VAT. Called from submitInvoice itself (not just the preview button) so
  // "Spara som utkast" - which persists to the backend without ever showing the preview - can't
  // bypass it. A fetch failure here fails open (returns true): this is an extra client-side guard
  // on top of the server, which doesn't validate this for credits at all, so nothing is lost by
  // letting submission proceed.
  const validateCreditInvoiceRowVat = async (
    formValues: InvoiceFormTypes.Create,
    sourceInvoice?: WintSuperkollAPIModel.Invoice
  ): Promise<boolean> => {
    // Note: sourceInvoice.credit reflects the persisted Invoice.Credit column, which isn't set on
    // the in-memory preview InvoiceModelFactory.CreateCreditNote returns for "kreditera och
    // redigera" - only creditedInvoiceId is (see submitInvoice's getValidationSchema call below,
    // which already uses this same field for the same reason).
    // noVatCompany mirrors the server's own exemption: a non-VAT-registered company's rows are
    // force-zeroed, so they can never match the article's rate and this check would block every
    // one of its credit notes.
    if (sourceInvoice?.creditedInvoiceId === undefined || !props.revenueAccountsEnabled || noVatCompany) {
      return true;
    }

    // The customer lookup is a fallback for the case where the user opens the preview before
    // CustomerInformationLoader has resolved; normally isDomesticCustomer is already hydrated.
    // It shares the article lookups' try: either one failing must fail open, since a rejected
    // promise here leaves the button doing nothing at all when clicked.
    let isDomesticCustomer = formValues.isDomesticCustomer;
    let articlesById: Map<number, WintSuperkollAPIModel.Article>;
    try {
      if (isDomesticCustomer === undefined && formValues.customer?.id) {
        const customer = await customerService.getCustomer(Number(formValues.customer.id));
        isDomesticCustomer = InvoiceHelper.isDomesticCountryCode(customer.billingAddress?.countryCode);
      }
      if (!isDomesticCustomer) {
        return true;
      }

      const articleIds = Array.from(
        new Set(
          (formValues.products ?? [])
            .map((product) => (product.article?.id ? Number(product.article.id) : undefined))
            .filter((id): id is number => !!id)
        )
      );

      articlesById = new Map(
        (await Promise.all(articleIds.map((id) => articleService.get(id)))).map((article) => [article.id, article])
      );
    } catch {
      return true;
    }

    for (const product of formValues.products ?? []) {
      const article = product.article?.id ? articlesById.get(Number(product.article.id)) : undefined;
      if (!article) continue;

      const isLocked = InvoiceHelper.isRowVatLocked(
        { articleAccount: article.account, articleVat: article.vat, articleIsAgentSale: article.isAgentSaleArticle },
        isDomesticCustomer,
        props.revenueAccountsEnabled,
        noVatCompany
      );
      if (!isLocked || product.tax === article.vat) {
        continue;
      }

      enqueueMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CreditRowVatMismatch, undefined, {
          articleName: article.text ?? "",
          articleVat: article.vat,
          rowVat: product.tax,
        }),
        StyleEnums.DisplayType.Danger
      );
      return false;
    }

    return true;
  };

  const submitInvoice = waitForForwardedCostAttachments(
    form,
    props.forwardedCosts,
    async (latestForm, validationSchema?: ObjectSchema<InvoiceFormTypes.Create>, asDraft?: boolean) => {
      const sourceInvoice = invoice ? (invoice as WintSuperkollAPIModel.Invoice) : undefined;
      if (!(await validateCreditInvoiceRowVat(latestForm.values, sourceInvoice))) {
        return;
      }

      return submitForm(
        latestForm,
        onInvoiceFormSubmit.bind(null, asDraft ?? false),
        validationSchema ??
          (asDraft
            ? getValidationSchema(
                false,
                false,
                true,
                invoice && "creditedInvoiceId" in invoice && invoice.creditedInvoiceId !== undefined,
                false
              )
            : undefined)
      );
    }
  );

  const submitRecurringInvoiceWithWarning = async (
    validationSchema?: ObjectSchema<InvoiceFormTypes.Create>,
    asDraft?: boolean
  ) => {
    const formValues = form.values;
    const calculatedTotal = invoiceService.calculateTotalFromProducts(formValues.products ?? []);
    const invoiceDate = formValues.recurringSettings?.recurringInvoiceStartDate || new Date();

    return showWarningDialogIfNeeded(
      calculatedTotal,
      formValues.currency,
      async () => {
        return await submitInvoice(validationSchema, asDraft);
      },
      invoiceDate
    );
  };

  const toggleRecurringInvoiceState = async () => {
    let id = props.invoice?.id;
    const isStart = props.invoice?.status !== WintSuperkollAPIModel.RecurringInvoiceState.Ongoing;

    if (isStart) {
      // Check for high invoice amount before starting recurring invoice
      const formValues = form.values;
      const calculatedTotal = invoiceService.calculateTotalFromProducts(formValues.products ?? []);
      const invoiceDate = formValues.recurringSettings?.recurringInvoiceStartDate || new Date();

      id = await showWarningDialogIfNeeded(
        calculatedTotal,
        formValues.currency,
        async () => {
          const submittedId = await submitInvoice(getValidationSchema(isRecurringInvoiceDraft));
          const invoiceId = submittedId || props.invoice?.id;
          if (invoiceId) {
            await recurringInvoiceService.start(invoiceId);
            reloadInvoice(invoiceId);
            return invoiceId;
          }
          throw new Error("No invoice ID available");
        },
        invoiceDate
      );

      return id;
    }

    if (id) {
      await recurringInvoiceService.stop(id);
      return id;
    }
  };

  const { showPreviewDialog } = useInvoicePreview();

  const onShowInvoicePreview = waitForForwardedCostAttachments(form, props.forwardedCosts, async (latestForm) => {
    const isValid = await validateForm(latestForm, TranslationKeys.Validations.FormContainsErrors);
    if (isValid) {
      const formValues = latestForm.values;
      const invoice = props.invoice ? (props.invoice as WintSuperkollAPIModel.Invoice) : undefined;

      if (!(await validateCreditInvoiceRowVat(formValues, invoice))) {
        return;
      }

      // Check for high invoice amount before showing preview (converting to SEK if needed)
      if (!isRecurring) {
        const calculatedTotal = invoiceService.calculateTotalFromProducts(formValues.products ?? []);
        const invoiceDate = formValues.otherSettings?.invoiceDate || new Date();

        try {
          await showWarningDialogIfNeeded(
            calculatedTotal,
            formValues.currency,
            async () => {
              // Small delay to let dialog close before opening preview
              setTimeout(() => {
                showPreviewDialog(
                  invoice,
                  invoiceService.convertFormToAPIRequest(formValues, noVatCompany, hideDeliveryDate, invoice),
                  invoiceService.formatInvoiceFormMasterRowConfigurations(formValues),
                  reloadInvoice
                );
              }, 100);
            },
            invoiceDate
          );
        } catch (error) {
          // If user cancelled, just return - don't show preview
          if (error instanceof Error && error.message === USER_CANCELLED_ERROR) {
            return;
          }
          throw error;
        }
      } else {
        // Recurring invoice - show preview directly
        showPreviewDialog(
          invoice,
          invoiceService.convertFormToAPIRequest(formValues, noVatCompany, hideDeliveryDate, invoice),
          invoiceService.formatInvoiceFormMasterRowConfigurations(formValues),
          reloadInvoice
        );
      }
    }
  });

  const isFormLoading = form.values.loadingCustomer !== null;

  return (
    <Portal container={footerMount}>
      {isRecurring ? (
        <RecurringInvoiceDraftFooter
          invoiceId={props.invoice?.id}
          status={props.invoice?.status as WintSuperkollAPIModel.RecurringInvoiceState}
          submitInvoice={submitRecurringInvoiceWithWarning}
          reloadInvoice={reloadInvoice}
          toggleRecurringInvoiceState={toggleRecurringInvoiceState}
          isFormLoading={isFormLoading}
          wizardProps={wizard}
        />
      ) : (
        <InvoiceDraftFooter
          invoiceId={props.invoice?.id}
          submitInvoice={submitInvoice}
          reloadInvoice={reloadInvoice}
          onShowInvoicePreview={onShowInvoicePreview}
          isFormLoading={isFormLoading}
          invoiceOwnerId={props.invoice?.ownerId}
          wizardProps={wizard}
          forwardedCosts={props.forwardedCosts}
        />
      )}
    </Portal>
  );
};

export default CreateInvoiceFooter;
